import React, { useCallback, useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { CollapsableCard } from '@app/app/_components/CollapsableCard/CollapsableCard';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { Heading } from '@app/app/_components/Heading/Heading';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { VehicleInformation } from '@app/domain/model/eshop/transport/VehicleInformation';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { CriterionOption } from '@app/domain/model/eshop/transport/CriterionOption';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { TransportInsuranceRequestResult } from '@app/service/eshop/transport/actions/requestTransportInsurance';
import { PaymentFrequencyPanel } from './PaymentFrequencyPanel';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { ExpandableText } from '@app/app/_components/ExpandableText/ExpandableText';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { useOwnerFormFields } from '@app/app/esales/transport/_sections/setups/useOwnerFormFields';
import { VehicleSummaryCard } from '@app/app/esales/transport/components/VehicleSummaryCard';
import { VehicleOwnerSummaryCard } from '@app/app/esales/transport/components/VehicleOwnerSummaryCard';
import { SummaryEditButton } from '@app/app/esales/transport/components/SummaryEditButton';
import { InsuranceStartDateSummaryPanel } from '@app/app/esales/transport/components/InsuranceStartDateSummaryPanel';
import { UpdateModal } from '@app/app/esales/transport/components/UpdateModal';
import { InsuranceDuration } from '@app/domain/model/eshop/transport/InsuranceDuration';
import { InsuranceConditionsPanel } from '@app/app/esales/transport/components/InsuranceConditionsPanel';

interface Props {
    vehicleInformation: VehicleInformation;
    formState: FormState<PurchaseTransportInsuranceResult>;
    selectedPurpose: Nullable<{ name: string; code: string }>;
    ownerAge: Nullable<CriterionOption>;
    experienceOtherDrivers: Nullable<CriterionOption>;
    territory: Nullable<{ name: string; code: string }>;
    ownerFormFields: ReturnType<typeof useOwnerFormFields>;
    totalPrice: number;
    priceToPayNow: number;
    selectedRiders: ProductDataRider[];
    insurancePackage: string;
    onNavigateToStep: (stepKey: string) => void;
    requestResult: TransportInsuranceRequestResult;
    setCascoStartDate: (startDate: string) => void;
    setMtplStartDate: (startDate: string) => void;
    cascoStartDate: string;
    mtplStartDate: string;
    paymentFrequency: number;
    onPaymentFrequencyChange?: (paymentFrequency: number) => void;
    isCompany: boolean;
    cascoPackageName: string;
}

export function TransportSummaryStep({
    vehicleInformation,
    formState,
    selectedPurpose,
    ownerAge,
    experienceOtherDrivers,
    territory,
    ownerFormFields,
    totalPrice,
    priceToPayNow,
    selectedRiders,
    insurancePackage,
    onNavigateToStep,
    requestResult,
    setCascoStartDate,
    setMtplStartDate,
    cascoStartDate,
    mtplStartDate,
    paymentFrequency,
    onPaymentFrequencyChange,
    isCompany,
    cascoPackageName,
}: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();
    const locale = useLocale();
    const parameters = useAppParameters();

    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [stepToNavigate, setStepToNavigate] = useState('');

    const policyLabel = useMemo(() => {
        if (insurancePackage === PremiumType.Mtpl) {
            return t('eshop.transport.policyType.mtpl');
        }
        if (insurancePackage === PremiumType.Casco) {
            return t('eshop.transport.policyType.casco');
        }
        if (insurancePackage === PremiumType.CascoMtpl) {
            return t('eshop.transport.policyType.mtpl_casco');
        }
        return '';
    }, [insurancePackage, t]);

    const handleOpenUpdateModal = useCallback((step: string) => {
        setUpdateModalOpen(true);
        setStepToNavigate(step);
    }, []);

    const handleCloseUpdateModal = useCallback(() => {
        setUpdateModalOpen(false);
        setStepToNavigate('');
    }, []);

    const navigateToStep = useCallback(() => {
        onNavigateToStep(stepToNavigate);
        handleCloseUpdateModal();
    }, [handleCloseUpdateModal, onNavigateToStep, stepToNavigate]);

    return (
        <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
                <VehicleSummaryCard
                    isCompany={isCompany}
                    vehicleInformation={vehicleInformation}
                    formState={formState}
                    selectedPurpose={selectedPurpose}
                    ownerAge={ownerAge}
                    experienceOtherDrivers={experienceOtherDrivers}
                    territory={territory}
                    handleOpenUpdateModal={handleOpenUpdateModal}
                    parameters={parameters}
                />

                <CollapsableCard label={t('eshop.transport.vehicleOwnerInformation')}>
                    <VehicleOwnerSummaryCard
                        ownerFormFields={ownerFormFields}
                        formState={formState}
                        isCompany={isCompany}
                    />
                </CollapsableCard>

                <CollapsableCard label={t('eshop.transport.policy')}>
                    <InsuranceConditionsPanel
                        policyLabel={policyLabel}
                        insuranceDuration={Number(formState.data?.formData.insuranceDuration ?? 0)}
                        totalPrice={totalPrice}
                        insurancePackage={insurancePackage}
                        cascoPackageName={cascoPackageName}
                        alarmLevel={formState.data?.formData.alarmLevelCasco}
                        deductiblePackageCode={formState.data?.formData.deductiblePackageCodeCasco}
                        handleOpenUpdateModal={handleOpenUpdateModal}
                    />

                    {formState.data?.formData.insuranceDuration === InsuranceDuration.TwelveMonths && (
                        <PaymentFrequencyPanel
                            key={paymentFrequency}
                            totalPrice={totalPrice}
                            priceToPayNow={priceToPayNow}
                            formState={formState}
                            paymentFrequency={paymentFrequency}
                            onPaymentFrequencyChange={onPaymentFrequencyChange}
                        />
                    )}

                    <InsuranceStartDateSummaryPanel
                        insuranceDuration={formState.data?.formData.insuranceDuration}
                        policyValidity={requestResult.policyValidity ?? null}
                        insurancePackage={insurancePackage}
                        setCascoStartDate={setCascoStartDate}
                        setMtplStartDate={setMtplStartDate}
                        cascoStartDate={cascoStartDate}
                        mtplStartDate={mtplStartDate}
                    />

                    {selectedRiders.length > 0 && (
                        <CollapsablePanel
                            label={t('eshop.transport.additionalRisks')}
                            addon={
                                <SummaryEditButton onClick={handleOpenUpdateModal} stepToNavigate="step-2-3">
                                    {t('general.edit')}
                                </SummaryEditButton>
                            }
                            collapsable={false}>
                            <div className="px-6">
                                {selectedRiders.map(rider => (
                                    <div
                                        className="border-b border-neutral-300 py-2 last:border-b-0"
                                        key={rider.riderGuid}>
                                        <Heading className="mb-4" level="h4">
                                            {rider.riderName}
                                        </Heading>
                                        <div className="text-md font-medium">
                                            <span>{formatEuroValue(rider.premiums.premium, tenant)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CollapsablePanel>
                    )}
                </CollapsableCard>
            </div>
            <div className="flex flex-col gap-6">
                <CheckboxInput
                    name="agreeToPrivacyPolicy"
                    error={formState.violations?.agreeToPrivacyPolicy}
                    label={t.rich('eshop.transport.privacyPolicyLabel', {
                        link: chunks => (
                            <Link
                                className="underline"
                                href={
                                    locale === 'lt'
                                        ? parameters.eshopInfoUrls.privacyPolicy
                                        : parameters.eshopInfoUrls.privacyPolicyEn
                                }
                                target="_blank">
                                {chunks}
                            </Link>
                        ),
                    })}
                />

                {insurancePackage === PremiumType.Mtpl && (
                    <CheckboxInput
                        name="agreeToMtplTerms"
                        error={formState.violations?.agreeToMtplTerms}
                        label={t.rich('eshop.transport.mtplTermsLabel', {
                            link: chunks => (
                                <Link
                                    className="underline"
                                    href={
                                        locale === 'lt'
                                            ? parameters.eshopInfoUrls.mtplTerms
                                            : parameters.eshopInfoUrls.mtplTermsEn
                                    }
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                        })}
                    />
                )}

                {insurancePackage === PremiumType.Casco && (
                    <CheckboxInput
                        name="agreeToCascoTerms"
                        error={formState.violations?.agreeToCascoTerms}
                        label={t.rich('eshop.transport.cascoTermsLabel', {
                            link: chunks => (
                                <Link
                                    className="underline"
                                    href={
                                        locale === 'lt'
                                            ? parameters.eshopInfoUrls.cascoTerms
                                            : parameters.eshopInfoUrls.cascoTermsEn
                                    }
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                        })}
                    />
                )}

                {insurancePackage === PremiumType.CascoMtpl && (
                    <>
                        <CheckboxInput
                            name="agreeToMtplTerms"
                            error={formState.violations?.agreeToMtplTerms}
                            label={t.rich('eshop.transport.mtplTermsLabel', {
                                link: chunks => (
                                    <Link
                                        className="underline"
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopInfoUrls.mtplTerms
                                                : parameters.eshopInfoUrls.mtplTermsEn
                                        }
                                        target="_blank">
                                        {chunks}
                                    </Link>
                                ),
                            })}
                        />
                        <CheckboxInput
                            name="agreeToCascoTerms"
                            error={formState.violations?.agreeToCascoTerms}
                            label={t.rich('eshop.transport.cascoTermsLabel', {
                                link: chunks => (
                                    <Link
                                        className="underline"
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopInfoUrls.cascoTerms
                                                : parameters.eshopInfoUrls.cascoTermsEn
                                        }
                                        target="_blank">
                                        {chunks}
                                    </Link>
                                ),
                            })}
                        />
                    </>
                )}

                <ExpandableText maxLines={3} className="text-gray-700 text-sm">
                    <div className="whitespace-pre-line">
                        {t.rich('eshop.transport.directMarketingInfoText', {
                            compensaEmail: chunks => (
                                <Link
                                    href="mailto:tiesioginerinkodara@compensa.lt"
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                            compensaLifeEmail: chunks => (
                                <Link
                                    href="mailto:tiesioginerinkodara@compensalife.lt"
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                            privacyPolicyLink: chunks => (
                                <Link
                                    href={
                                        locale === 'lt'
                                            ? parameters.eshopInfoUrls.privacyPolicy
                                            : parameters.eshopInfoUrls.privacyPolicyEn
                                    }
                                    className="hover:text-blue-800 text-blue-600 underline"
                                    target="_blank">
                                    {chunks}
                                </Link>
                            ),
                        })}
                    </div>
                </ExpandableText>

                <div className="flex flex-col gap-4">
                    <Paragraph isBold>{t('eshop.transport.directMarketingAgreement')}</Paragraph>
                    <CheckboxInput
                        name="agreeToDirectMarketingCompensa"
                        label={t('profile.directMarketingConsentCompensaLabel')}
                    />
                    <CheckboxInput
                        name="agreeToDirectMarketingCompensaLife"
                        label={t('profile.directMarketingConsentCompensaLifeLabel')}
                    />
                </div>
            </div>
            <UpdateModal
                updateModalOpen={updateModalOpen}
                handleCloseUpdateModal={handleCloseUpdateModal}
                navigateToStep={navigateToStep}
            />
        </div>
    );
}
